import React, { useMemo, useState } from "react";
import { Save, X, Plus, Trash2, ArrowUp, ArrowDown, AlertTriangle, Star } from "lucide-react";
import { api, getAwgPerUsd } from "@/lib/api";
import { BylineEditor } from "@/pages/admin/fields/BylineEditor";
import { Section } from "@/pages/admin/fields/primitives";
import { PopularPreview } from "@/components/popular/PopularPreview";
import { CommercialSection } from "@/pages/admin/fields/CommercialSection";
import { DISCOVERY_GROUPS } from "@/lib/discoveryGroups";

// ---------- helpers ----------
const rid = (prefix) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
const newTier = () => ({ id: rid("tier"), name: "", price: 0, description: "", features: [], popular: false, request_estimate: false, recurring_price: 0, recurring_label: "" });
const newAddon = () => ({ id: rid("addon"), name: "", price: 0, description: "", popular: false, tier_ids: [], request_estimate: false, recurring_price: 0, recurring_label: "", replaces_base_recurring: false });
const newItem = () => ({ id: rid("item"), name: "", price: 0, description: "", display_order: 0, active: true, popular: false, request_estimate: false, recurring_price: 0, recurring_label: "" });
const newKit = () => ({ id: rid("kit"), name: "", description: "", included_item_ids: [], bundle_price: 0, display_order: 0, active: true, popular: false, request_estimate: false, recurring_price: 0, recurring_label: "" });
// Phase 2 CMS Reconciliation (Feb 2026) — finder-tile row factory.
const newFinderItem = () => ({
  key: rid("finder"),
  label: "",
  icon_key: "",
  focus_param: "",
  examples: "",
  href: "",
  discovery_groups: [],
  show_on_home: false,
  show_on_services: true,
  order: 0,
});

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

const PRICING_MODELS = [
  { key: "tiered", label: "Tiered — Depth-based packages (Basic / Growth / Enterprise)" },
  { key: "volume_tiered", label: "Volume Tiered — Same UI, tiers describe quantity/cadence" },
  { key: "custom_items", label: "Custom Items — Flat priced checklist, no bundling" },
  { key: "kits", label: "Kits — Named situational bundles referencing item pool" },
];

// ---------- Reusable atoms ----------
const Field = ({ label, hint, children }) => (
  <div>
    {label && <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">{label}</label>}
    {children}
    {hint && <div className="text-[11px] text-[#7A7A7A] mt-1">{hint}</div>}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    className={`mt-2 w-full bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#FF6600] ${props.className || ""}`}
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    rows={props.rows || 3}
    className={`mt-2 w-full bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#FF6600] resize-none ${props.className || ""}`}
  />
);

// AWG input with live "≈ $X USD" preview beside it
const PriceInput = ({ value, onChange, testId, placeholder = "0" }) => {
  const rate = getAwgPerUsd() || 1.79;
  const usd = value ? Math.round(Number(value || 0) / rate) : 0;
  return (
    <div className="mt-2 flex items-center gap-2">
      <div className="flex-1 relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#7A7A7A] font-semibold">AWG</span>
        <input
          type="number"
          min="0"
          step="1"
          placeholder={placeholder}
          value={value ?? ""}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          data-testid={testId}
          className="w-full bg-[#1A1A1A] border border-[#262626] rounded-xl pl-12 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#FF6600]"
        />
      </div>
      <div className="text-xs text-[#FF6600] font-semibold whitespace-nowrap min-w-[80px]">
        ≈ ${usd.toLocaleString()} USD
      </div>
    </div>
  );
};

const RowControls = ({ onUp, onDown, onDelete, disabledUp, disabledDown }) => (
  <div className="flex flex-col gap-1 shrink-0">
    <button type="button" onClick={onUp} disabled={disabledUp} className="w-7 h-7 rounded-md border border-[#262626] grid place-items-center disabled:opacity-30 hover:border-[#FF6600]"><ArrowUp className="w-3.5 h-3.5" /></button>
    <button type="button" onClick={onDown} disabled={disabledDown} className="w-7 h-7 rounded-md border border-[#262626] grid place-items-center disabled:opacity-30 hover:border-[#FF6600]"><ArrowDown className="w-3.5 h-3.5" /></button>
    <button type="button" onClick={onDelete} className="w-7 h-7 rounded-md border border-[#262626] grid place-items-center hover:border-red-500 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
  </div>
);

// ---------- Sub-editors ----------
const ChecklistEditor = ({ items, onChange }) => (
  <div className="space-y-2">
    {(items || []).map((it, i) => (
      <div key={i} className="flex items-center gap-2">
        <Input value={it} onChange={(e) => onChange(items.map((x, k) => (k === i ? e.target.value : x)))} placeholder={`Bullet ${i + 1}`} className="!mt-0" />
        <RowControls
          onUp={() => onChange(move(items, i, -1))}
          onDown={() => onChange(move(items, i, 1))}
          onDelete={() => onChange(items.filter((_, k) => k !== i))}
          disabledUp={i === 0}
          disabledDown={i === items.length - 1}
        />
      </div>
    ))}
    <button type="button" onClick={() => onChange([...(items || []), ""])} className="text-xs text-[#FF6600] font-semibold inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add bullet</button>
  </div>
);

const TiersEditor = ({ tiers, onChange }) => (
  <div className="space-y-4">
    {(tiers || []).map((t, i) => (
      <div key={t.id} className="border border-[#262626] rounded-xl p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="flex-1 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name"><Input value={t.name} onChange={(e) => onChange(tiers.map((x, k) => (k === i ? { ...x, name: e.target.value } : x)))} placeholder="e.g. Growth" /></Field>
              <Field label="Popular?">
                <label className="mt-2 flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!t.popular} onChange={(e) => onChange(tiers.map((x, k) => (k === i ? { ...x, popular: e.target.checked } : x)))} className="w-4 h-4 accent-[#FF6600]" />
                  <span className="inline-flex items-center gap-1 text-xs"><Star className="w-3 h-3 text-[#FF6600]" /> Marked as popular</span>
                </label>
              </Field>
            </div>
            {t.popular && (
              <PopularPreview kind="card" name={t.name} testId={`tier-${i}-popular-preview`} />
            )}
            <Field label="Description"><Input value={t.description} onChange={(e) => onChange(tiers.map((x, k) => (k === i ? { ...x, description: e.target.value } : x)))} placeholder="Short subtitle" /></Field>
            <Field label="Price">
              <PriceInput value={t.price} onChange={(v) => onChange(tiers.map((x, k) => (k === i ? { ...x, price: v } : x)))} />
            </Field>
            <Field label="Features (one per line)">
              <div className="space-y-2 mt-2">
                {(t.features || []).map((f, j) => (
                  <div key={j} className="flex items-center gap-2">
                    <Input value={f} onChange={(e) => onChange(tiers.map((x, k) => (k === i ? { ...x, features: x.features.map((y, m) => (m === j ? e.target.value : y)) } : x)))} placeholder="Feature" className="!mt-0" />
                    <button type="button" onClick={() => onChange(tiers.map((x, k) => (k === i ? { ...x, features: x.features.filter((_, m) => m !== j) } : x)))} className="w-7 h-7 rounded-md border border-[#262626] grid place-items-center hover:border-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <button type="button" onClick={() => onChange(tiers.map((x, k) => (k === i ? { ...x, features: [...(x.features || []), ""] } : x)))} className="text-xs text-[#FF6600] font-semibold inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add feature</button>
              </div>
            </Field>
            <CommercialSection
              value={t}
              scope="tier"
              onChange={(next) => onChange(tiers.map((x, k) => (k === i ? next : x)))}
              testId={`tier-${i}-commercial`}
            />
          </div>
          <RowControls
            onUp={() => onChange(move(tiers, i, -1))}
            onDown={() => onChange(move(tiers, i, 1))}
            onDelete={() => onChange(tiers.filter((_, k) => k !== i))}
            disabledUp={i === 0}
            disabledDown={i === tiers.length - 1}
          />
        </div>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...(tiers || []), newTier()])} className="w-full border border-dashed border-[#262626] hover:border-[#FF6600] rounded-xl py-3 text-sm text-[#A3A3A3] hover:text-[#FF6600] inline-flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add tier</button>
  </div>
);

const AddonsEditor = ({ addons, tiers = [], onChange }) => (
  <div className="space-y-3">
    {(addons || []).map((a, i) => (
      <div key={a.id} className="border border-[#262626] rounded-xl p-3 flex items-start gap-3">
        <div className="flex-1 space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <Input value={a.name} onChange={(e) => onChange(addons.map((x, k) => (k === i ? { ...x, name: e.target.value } : x)))} placeholder="Add-on name" className="!mt-0" />
            <div className="flex items-center">
              <PriceInput value={a.price} onChange={(v) => onChange(addons.map((x, k) => (k === i ? { ...x, price: v } : x)))} />
            </div>
          </div>
          <Input value={a.description || ""} onChange={(e) => onChange(addons.map((x, k) => (k === i ? { ...x, description: e.target.value } : x)))} placeholder="Optional description" className="!mt-0" />
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={!!a.popular} onChange={(e) => onChange(addons.map((x, k) => (k === i ? { ...x, popular: e.target.checked } : x)))} className="w-4 h-4 accent-[#FF6600]" />
            <span className="inline-flex items-center gap-1"><Star className="w-3 h-3 text-[#FF6600]" /> Marked as popular</span>
          </label>
          {a.popular && (
            <PopularPreview kind="row" name={a.name} testId={`addon-${i}-popular-preview`} />
          )}
          <CommercialSection
            value={a}
            scope="addon"
            tiers={tiers}
            onChange={(next) => onChange(addons.map((x, k) => (k === i ? next : x)))}
            testId={`addon-${i}-commercial`}
          />
        </div>
        <RowControls onUp={() => onChange(move(addons, i, -1))} onDown={() => onChange(move(addons, i, 1))} onDelete={() => onChange(addons.filter((_, k) => k !== i))} disabledUp={i === 0} disabledDown={i === addons.length - 1} />
      </div>
    ))}
    <button type="button" onClick={() => onChange([...(addons || []), newAddon()])} className="w-full border border-dashed border-[#262626] hover:border-[#FF6600] rounded-xl py-3 text-sm text-[#A3A3A3] hover:text-[#FF6600] inline-flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add add-on</button>
  </div>
);

const StandaloneItemsEditor = ({ items, onChange }) => {
  // Sibling item_codes available as dependency / requires-one-of targets.
  const siblingChoices = (items || [])
    .filter((i) => (i.item_code || "").trim())
    .map((i) => ({ code: i.item_code, name: i.name || i.item_code }));
  // Existing option groups already used in this service — plus a friendly
  // "(none)" and a controlled "+ Create new" affordance below.
  const existingGroups = Array.from(new Set(
    (items || []).map((i) => (i.option_group || "").trim()).filter(Boolean)
  ));
  return (
    <div className="space-y-3">
      {(items || []).map((it, i) => (
        <div key={it.id} className="border border-[#262626] rounded-xl p-3 flex items-start gap-3">
          <div className="flex-1 space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <Input value={it.name} onChange={(e) => onChange(items.map((x, k) => (k === i ? { ...x, name: e.target.value } : x)))} placeholder="Item name" className="!mt-0" />
              <PriceInput value={it.price} onChange={(v) => onChange(items.map((x, k) => (k === i ? { ...x, price: v } : x)))} />
            </div>
            <Input value={it.description || ""} onChange={(e) => onChange(items.map((x, k) => (k === i ? { ...x, description: e.target.value } : x)))} placeholder="Optional description" className="!mt-0" />
            {it.item_code ? (
              <div className="flex items-center gap-2 text-[10px]">
                <span className="text-[#666] uppercase tracking-widest">Item Code</span>
                <span
                  className="font-mono px-2 py-0.5 rounded bg-[#141414] border border-[#262626] text-[#A3A3A3]"
                  title="Immutable commercial identity — never changes"
                  data-testid={`item-${i}-code`}
                >{it.item_code}</span>
              </div>
            ) : null}
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={it.active !== false} onChange={(e) => onChange(items.map((x, k) => (k === i ? { ...x, active: e.target.checked } : x)))} className="w-4 h-4 accent-[#FF6600]" />
              Active
            </label>
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer ml-4">
              <input type="checkbox" checked={!!it.popular} onChange={(e) => onChange(items.map((x, k) => (k === i ? { ...x, popular: e.target.checked } : x)))} className="w-4 h-4 accent-[#FF6600]" />
              <span className="inline-flex items-center gap-1"><Star className="w-3 h-3 text-[#FF6600]" /> Marked as popular</span>
            </label>
            {it.popular && (
              <PopularPreview kind="row" name={it.name} testId={`item-${i}-popular-preview`} />
            )}

            {/* P1 — Dependency + mutex + quantity editors. All optional. */}
            <RequiresEditor
              value={it}
              siblings={siblingChoices}
              onChange={(next) => onChange(items.map((x, k) => (k === i ? next : x)))}
              testId={`item-${i}`}
            />
            <OptionGroupEditor
              value={it}
              existingGroups={existingGroups}
              onChange={(next) => onChange(items.map((x, k) => (k === i ? next : x)))}
              testId={`item-${i}`}
            />
            <QuantityEditor
              value={it}
              onChange={(next) => onChange(items.map((x, k) => (k === i ? next : x)))}
              testId={`item-${i}`}
            />

            <CommercialSection
              value={it}
              scope="item"
              onChange={(next) => onChange(items.map((x, k) => (k === i ? next : x)))}
              testId={`item-${i}-commercial`}
            />
          </div>
          <RowControls onUp={() => onChange(move(items, i, -1))} onDown={() => onChange(move(items, i, 1))} onDelete={() => onChange(items.filter((_, k) => k !== i))} disabledUp={i === 0} disabledDown={i === items.length - 1} />
        </div>
      ))}
      <button type="button" onClick={() => onChange([...(items || []), newItem()])} className="w-full border border-dashed border-[#262626] hover:border-[#FF6600] rounded-xl py-3 text-sm text-[#A3A3A3] hover:text-[#FF6600] inline-flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add item</button>
    </div>
  );
};

// -- P1 editors ------------------------------------------------------------

const RequiresEditor = ({ value, siblings, onChange, testId }) => {
  const anyList = Array.isArray(value.depends_on_item_code_any) ? value.depends_on_item_code_any : [];
  return (
    <div className="mt-2 pt-2 border-t border-[#1F1F1F] space-y-2">
      <div className="text-[10px] uppercase tracking-widest text-[#666]">Dependency</div>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs">
          <span className="text-[#A3A3A3]">Requires</span>
          <select
            value={value.depends_on_item_code || ""}
            onChange={(e) => onChange({ ...value, depends_on_item_code: e.target.value })}
            className="w-full mt-1 bg-[#0F0F0F] border border-[#262626] rounded px-2 py-1.5 text-sm"
            data-testid={`${testId}-requires`}
          >
            <option value="">(no dependency)</option>
            {siblings.map((s) => (
              <option key={s.code} value={s.code}>{s.name} · {s.code}</option>
            ))}
          </select>
        </label>
        <label className="block text-xs">
          <span className="text-[#A3A3A3]">Requires one of (any)</span>
          <select
            multiple
            value={anyList}
            onChange={(e) => onChange({ ...value, depends_on_item_code_any: Array.from(e.target.selectedOptions, (o) => o.value) })}
            className="w-full mt-1 bg-[#0F0F0F] border border-[#262626] rounded px-2 py-1.5 text-sm min-h-[68px]"
            data-testid={`${testId}-requires-any`}
          >
            {siblings.map((s) => (
              <option key={s.code} value={s.code}>{s.name}</option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
};

const OptionGroupEditor = ({ value, existingGroups, onChange, testId }) => {
  const current = value.option_group || "";
  const [creating, setCreating] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  return (
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-widest text-[#666]">Option group (exclusivity)</div>
      {!creating ? (
        <div className="flex gap-2 items-center">
          <select
            value={current}
            onChange={(e) => onChange({ ...value, option_group: e.target.value })}
            className="flex-1 bg-[#0F0F0F] border border-[#262626] rounded px-2 py-1.5 text-sm"
            data-testid={`${testId}-option-group`}
          >
            <option value="">(none — this item is independent)</option>
            {existingGroups.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="text-xs text-[#FF6600] hover:underline"
            data-testid={`${testId}-option-group-new`}
          >+ Create new</button>
        </div>
      ) : (
        <div className="flex gap-2 items-center">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="new_group_id (snake_case)"
            className="!mt-0 flex-1"
          />
          <button
            type="button"
            onClick={() => {
              const g = draft.trim();
              if (!g) return;
              onChange({ ...value, option_group: g });
              setCreating(false); setDraft("");
            }}
            className="text-xs bg-[#FF6600] text-black px-3 py-1.5 rounded font-semibold"
          >Save</button>
          <button
            type="button"
            onClick={() => { setCreating(false); setDraft(""); }}
            className="text-xs text-[#A3A3A3]"
          >Cancel</button>
        </div>
      )}
      {current && (
        <div className="text-[10px] text-[#666]">Only one selected item in this group can be purchased per configuration.</div>
      )}
    </div>
  );
};

const _quantityScheduleError = (schedule) => {
  if (!Array.isArray(schedule) || schedule.length === 0) return null;
  const mins = schedule.map((r) => Number(r.min_quantity));
  if (mins.some((m) => !Number.isFinite(m) || m <= 0)) return "min_quantity must be a positive integer";
  if (new Set(mins).size !== mins.length) return "min_quantity thresholds must be unique";
  for (let i = 1; i < mins.length; i++) if (mins[i] <= mins[i - 1]) return "min_quantity must ascend";
  const prices = schedule.map((r) => Number(r.unit_price));
  if (prices.some((p) => !Number.isFinite(p) || p < 0)) return "unit_price must be non-negative";
  return null;
};

const hasInvalidQuantityPricing = (items) =>
  (items || []).some((it) => !!_quantityScheduleError(it && it.quantity_pricing));

const QuantityEditor = ({ value, onChange, testId }) => {
  const enabled = !!value.quantity_enabled;
  const schedule = Array.isArray(value.quantity_pricing) ? value.quantity_pricing : [];
  const updateSchedule = (rows) => onChange({ ...value, quantity_pricing: rows });
  const scheduleError = _quantityScheduleError(schedule);
  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-widest text-[#666]">Quantity</div>
      <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onChange({ ...value, quantity_enabled: e.target.checked, quantity_max: e.target.checked ? (value.quantity_max || 10) : 1 })}
          className="w-4 h-4 accent-[#FF6600]"
          data-testid={`${testId}-qty-enabled`}
        />
        Customer can buy multiple
      </label>
      {enabled && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs">
              <span className="text-[#A3A3A3]">Min quantity</span>
              <Input type="number" min="1" value={value.quantity_min ?? 1}
                onChange={(e) => onChange({ ...value, quantity_min: Number(e.target.value) || 1 })}
                className="!mt-1" data-testid={`${testId}-qty-min`} />
            </label>
            <label className="block text-xs">
              <span className="text-[#A3A3A3]">Max quantity</span>
              <Input type="number" min="1" value={value.quantity_max ?? 10}
                onChange={(e) => onChange({ ...value, quantity_max: Number(e.target.value) || 1 })}
                className="!mt-1" data-testid={`${testId}-qty-max`} />
            </label>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#666] mt-2">Quantity pricing (optional)</div>
          <div className="text-[10px] text-[#666] -mt-1">Progressive schedule. Nth unit priced at the row whose Minimum Quantity is the largest &le; N. Leave empty for flat price.</div>
          <div className="space-y-1">
            {schedule.map((row, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input type="number" min="1" value={row.min_quantity} placeholder="Min qty"
                  onChange={(e) => updateSchedule(schedule.map((r, k) => (k === i ? { ...r, min_quantity: Number(e.target.value) } : r)))}
                  className="!mt-0 flex-1" data-testid={`${testId}-qty-schedule-${i}-min`} />
                <PriceInput value={row.unit_price}
                  onChange={(v) => updateSchedule(schedule.map((r, k) => (k === i ? { ...r, unit_price: v } : r)))} />
                <button type="button" onClick={() => updateSchedule(schedule.filter((_, k) => k !== i))} className="text-xs text-[#A3A3A3]">×</button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => updateSchedule([...schedule, { min_quantity: schedule.length > 0 ? Number(schedule[schedule.length - 1].min_quantity || 0) + 1 : 1, unit_price: 0 }])}
              className="text-xs text-[#FF6600] hover:underline"
              data-testid={`${testId}-qty-schedule-add`}
            >+ Add threshold</button>
          </div>
          {scheduleError && (
            <div className="text-[11px] text-red-400">{scheduleError}</div>
          )}
        </>
      )}
    </div>
  );
};

// ---------- Package Inclusions v1 editors ----------
// `marketing_features[]` — freeform ordered copy shown on Step 1 kit
// cards. Purely presentation. NEVER consulted for pricing / entitlement.
const MarketingFeaturesEditor = ({ value = [], onChange, testId }) => {
  const rows = Array.isArray(value) ? value : [];
  const update = (idx, next) => onChange(rows.map((r, k) => (k === idx ? next : r)));
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[#666]">Marketing features</div>
      <div className="text-[10px] text-[#666] -mt-1">Freeform copy for the Step 1 package card. Safe to reword — never used for pricing or entitlement.</div>
      {rows.map((row, idx) => (
        <div key={idx} className="flex gap-2 items-center">
          <Input
            value={row}
            onChange={(e) => update(idx, e.target.value)}
            placeholder="e.g. Perfect for one-off event launches"
            className="!mt-0 flex-1"
            data-testid={`${testId}-row-${idx}`}
          />
          <button type="button" onClick={() => onChange(rows.filter((_, k) => k !== idx))} className="text-xs text-[#A3A3A3]">×</button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...rows, ""])}
        className="text-xs text-[#FF6600] hover:underline"
        data-testid={`${testId}-add`}
      >+ Add feature</button>
    </div>
  );
};

// `included_items[]` — structured entitlement. Each row carries a stable
// `code`, editable `name`, and `quantity`. When a `code` matches a
// sellable catalog item_code (in this service's standalone_items pool),
// the strip on the customer configurator prefers the catalog item's
// current name so Dashboard renames propagate automatically.
const IncludedItemsEditor = ({ value = [], catalogItems = [], onChange, testId }) => {
  const rows = Array.isArray(value) ? value : [];
  const update = (idx, patch) => onChange(rows.map((r, k) => (k === idx ? { ...r, ...patch } : r)));
  const catalogByCode = new Map(
    (catalogItems || [])
      .filter((c) => c && c.item_code)
      .map((c) => [c.item_code, c]),
  );
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[#666]">Included items (entitlement)</div>
      <div className="text-[10px] text-[#666] -mt-1">Structured "Already Included" rows. `code` should match an existing catalog item_code when the deliverable is sellable, otherwise use a stable `deliverable_code` for package-only entitlements.</div>
      {rows.length === 0 && <div className="text-xs text-[#7A7A7A] italic">No inclusions defined.</div>}
      {rows.map((row, idx) => {
        const catalogHit = catalogByCode.get((row.code || "").trim());
        const inheritedName = catalogHit ? catalogHit.name : null;
        return (
          <div key={idx} className="rounded-md border border-[#1F1F1F] bg-[#0F0F0F] p-2 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-[11px]">
                <span className="text-[#7A7A7A]">Code</span>
                <Input
                  value={row.code || ""}
                  onChange={(e) => update(idx, { code: e.target.value.trim() })}
                  placeholder="ecp_deliverable_static_story"
                  className="!mt-1 font-mono text-xs"
                  data-testid={`${testId}-row-${idx}-code`}
                />
              </label>
              <label className="block text-[11px]">
                <span className="text-[#7A7A7A]">Quantity</span>
                <Input
                  type="number"
                  min="1"
                  value={row.quantity ?? 1}
                  onChange={(e) => update(idx, { quantity: Number(e.target.value) || 1 })}
                  className="!mt-1"
                  data-testid={`${testId}-row-${idx}-quantity`}
                />
              </label>
            </div>
            <label className="block text-[11px]">
              <span className="text-[#7A7A7A]">Display name {inheritedName ? "(fallback — catalog name wins)" : ""}</span>
              <Input
                value={row.name || ""}
                onChange={(e) => update(idx, { name: e.target.value })}
                placeholder={inheritedName || "e.g. Static Story"}
                className="!mt-1"
                data-testid={`${testId}-row-${idx}-name`}
              />
            </label>
            {inheritedName && (
              <div className="text-[10px] text-[#FF6600]/80">
                Resolves to catalog item: <span className="font-mono">{catalogHit.item_code}</span> — “{inheritedName}”
              </div>
            )}
            <div className="flex justify-end">
              <button type="button" onClick={() => onChange(rows.filter((_, k) => k !== idx))} className="text-xs text-[#A3A3A3]" data-testid={`${testId}-row-${idx}-remove`}>Remove</button>
            </div>
          </div>
        );
      })}
      <button
        type="button"
        onClick={() => onChange([...rows, { code: "", name: "", quantity: 1 }])}
        className="text-xs text-[#FF6600] hover:underline"
        data-testid={`${testId}-add`}
      >+ Add included item</button>
    </div>
  );
};

const KitsEditor = ({ kits, items, onChange }) => {
  return (
    <div className="space-y-4">
      {(kits || []).map((k, i) => {
        const includedItems = (items || []).filter((it) => (k.included_item_ids || []).includes(it.id));
        const includedSum = includedItems.reduce((s, it) => s + Number(it.price || 0), 0);
        const overpriced = Number(k.bundle_price || 0) > includedSum && includedSum > 0;
        return (
          <div key={k.id} className="border border-[#262626] rounded-xl p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-1 space-y-3">
                <Field label="Kit name"><Input value={k.name} onChange={(e) => onChange(kits.map((x, m) => (m === i ? { ...x, name: e.target.value } : x)))} placeholder="e.g. Event Launch Kit" /></Field>
                <Field label="Description"><Textarea value={k.description || ""} onChange={(e) => onChange(kits.map((x, m) => (m === i ? { ...x, description: e.target.value } : x)))} placeholder="What situation is this kit for?" /></Field>
                <Field label="Included items" hint="Pick from this service's standalone item pool. Empty pool? Add items first below.">
                  <div className="mt-2 space-y-1 max-h-48 overflow-y-auto pr-1">
                    {(items || []).length === 0 && <div className="text-xs text-[#7A7A7A] italic">No items in pool yet.</div>}
                    {(items || []).map((it) => {
                      const checked = (k.included_item_ids || []).includes(it.id);
                      return (
                        <label key={it.id} className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-[#151515] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...(k.included_item_ids || []), it.id]
                                : (k.included_item_ids || []).filter((x) => x !== it.id);
                              onChange(kits.map((x, m) => (m === i ? { ...x, included_item_ids: next } : x)));
                            }}
                            className="w-4 h-4 accent-[#FF6600]"
                          />
                          <span className="text-sm flex-1">{it.name || <span className="italic text-[#7A7A7A]">(unnamed)</span>}</span>
                          <span className="text-xs text-[#7A7A7A]">AWG {Number(it.price || 0).toLocaleString()}</span>
                        </label>
                      );
                    })}
                  </div>
                </Field>
                <Field label="Bundle price" hint={`Sum of included items: AWG ${includedSum.toLocaleString()}. Bundle price should typically be less than the sum.`}>
                  <PriceInput value={k.bundle_price} onChange={(v) => onChange(kits.map((x, m) => (m === i ? { ...x, bundle_price: v } : x)))} />
                </Field>
                {overpriced && (
                  <div className="flex items-start gap-2 text-xs text-amber-400 bg-amber-400/10 border border-amber-400/30 rounded-lg px-3 py-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    Bundle price (AWG {Number(k.bundle_price || 0).toLocaleString()}) is higher than the sum of included items (AWG {includedSum.toLocaleString()}). This defeats the purpose of a bundle discount — proceed only if intentional.
                  </div>
                )}
                <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={k.active !== false} onChange={(e) => onChange(kits.map((x, m) => (m === i ? { ...x, active: e.target.checked } : x)))} className="w-4 h-4 accent-[#FF6600]" />
                  Active
                </label>
                <label className="inline-flex items-center gap-2 text-xs cursor-pointer ml-4">
                  <input type="checkbox" checked={!!k.popular} onChange={(e) => onChange(kits.map((x, m) => (m === i ? { ...x, popular: e.target.checked } : x)))} className="w-4 h-4 accent-[#FF6600]" />
                  <span className="inline-flex items-center gap-1"><Star className="w-3 h-3 text-[#FF6600]" /> Marked as popular</span>
                </label>
                {k.popular && (
                  <PopularPreview kind="card" name={k.name} testId={`kit-${i}-popular-preview`} />
                )}
                <CommercialSection
                  value={k}
                  scope="kit"
                  onChange={(next) => onChange(kits.map((x, m) => (m === i ? next : x)))}
                  testId={`kit-${i}-commercial`}
                />
                <MarketingFeaturesEditor
                  value={k.marketing_features || []}
                  onChange={(next) => onChange(kits.map((x, m) => (m === i ? { ...x, marketing_features: next } : x)))}
                  testId={`kit-${i}-marketing-features`}
                />
                <IncludedItemsEditor
                  value={k.included_items || []}
                  catalogItems={items || []}
                  onChange={(next) => onChange(kits.map((x, m) => (m === i ? { ...x, included_items: next } : x)))}
                  testId={`kit-${i}-included-items`}
                />
              </div>
              <RowControls onUp={() => onChange(move(kits, i, -1))} onDown={() => onChange(move(kits, i, 1))} onDelete={() => onChange(kits.filter((_, m) => m !== i))} disabledUp={i === 0} disabledDown={i === kits.length - 1} />
            </div>
          </div>
        );
      })}
      <button type="button" onClick={() => onChange([...(kits || []), newKit()])} className="w-full border border-dashed border-[#262626] hover:border-[#FF6600] rounded-xl py-3 text-sm text-[#A3A3A3] hover:text-[#FF6600] inline-flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add kit</button>
    </div>
  );
};

// ---------- Finder Shortcuts editor (Phase 2 CMS Reconciliation) ----------
const FinderItemsEditor = ({ items, onChange, testId }) => {
  const rows = Array.isArray(items) ? items : [];
  const update = (idx, patch) => onChange(rows.map((r, k) => (k === idx ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-3" data-testid={testId}>
      {rows.length === 0 && (
        <div className="text-xs text-[#7A7A7A] italic">
          No finder tiles configured. This service will not appear on the homepage or /services shortcut grids.
        </div>
      )}
      {rows.map((row, idx) => (
        <div key={row.key || idx} className="border border-[#262626] rounded-xl p-3 space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tile Label">
              <Input
                value={row.label || ""}
                onChange={(e) => update(idx, { label: e.target.value })}
                placeholder="e.g. Menu / Price List"
                className="!mt-1"
                data-testid={`${testId}-row-${idx}-label`}
              />
            </Field>
            <Field label="Icon Key" hint="Basename of /public/finder-icons/{key}.png (e.g. logo, flyer, menu).">
              <Input
                value={row.icon_key || ""}
                onChange={(e) => update(idx, { icon_key: e.target.value.trim() })}
                placeholder="brochure"
                className="!mt-1 font-mono text-xs"
                data-testid={`${testId}-row-${idx}-icon`}
              />
            </Field>
          </div>
          <Field label="Homepage Examples" hint="Short curated deliverables line under the homepage card title, e.g. Menus · Business Cards · Brochures. Home tiles only; keep to 3–4 items.">
            <Input
              value={row.examples || ""}
              onChange={(e) => update(idx, { examples: e.target.value })}
              placeholder="Menus · Business Cards · Brochures"
              className="!mt-1 text-xs"
              data-testid={`${testId}-row-${idx}-examples`}
            />
          </Field>
          <Field label="Discovery Groups" hint="Public categories this tile appears under on /services (a tile may sit in several; it always links to this service's one canonical page).">
            <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`${testId}-row-${idx}-groups`}>
              {DISCOVERY_GROUPS.map((g) => {
                const on = (row.discovery_groups || []).includes(g.key);
                return (
                  <button
                    key={g.key}
                    type="button"
                    onClick={() => {
                      const cur = row.discovery_groups || [];
                      update(idx, { discovery_groups: on ? cur.filter((k) => k !== g.key) : [...cur, g.key] });
                    }}
                    data-testid={`${testId}-row-${idx}-group-${g.key}`}
                    className={`px-2.5 py-1 rounded-full border text-[11px] font-semibold ${on ? "bg-[#FF6600] border-[#FF6600] text-black" : "border-[#262626] text-[#A3A3A3] hover:text-white"}`}
                  >
                    {g.label}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Destination Override" hint="Optional. Leave empty to link to this service (plus Focus Param). Use e.g. /services?group=print-signage#services-finder for a category card.">
            <Input
              value={row.href || ""}
              onChange={(e) => update(idx, { href: e.target.value })}
              placeholder="/services?group=print-signage#services-finder"
              className="!mt-1 text-xs"
              data-testid={`${testId}-row-${idx}-href`}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Focus Param" hint="Optional ?focus=<value> deep-link. Scrolls the family into view. Never triggers a paid selection.">
              <Input
                value={row.focus_param || ""}
                onChange={(e) => update(idx, { focus_param: e.target.value.trim() })}
                placeholder="essentials"
                className="!mt-1 font-mono text-xs"
                data-testid={`${testId}-row-${idx}-focus`}
              />
            </Field>
            <Field label="Order" hint="Display order within its surface (Home / Services). Lower renders first.">
              <Input
                type="number"
                value={row.order ?? 0}
                onChange={(e) => update(idx, { order: Number(e.target.value) || 0 })}
                className="!mt-1"
                data-testid={`${testId}-row-${idx}-order`}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={!!row.show_on_home}
                onChange={(e) => update(idx, { show_on_home: e.target.checked })}
                className="w-4 h-4 accent-[#FF6600]"
                data-testid={`${testId}-row-${idx}-show-home`}
              />
              Show on Homepage finder (max 7)
            </label>
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={!!row.show_on_services}
                onChange={(e) => update(idx, { show_on_services: e.target.checked })}
                className="w-4 h-4 accent-[#FF6600]"
                data-testid={`${testId}-row-${idx}-show-services`}
              />
              Show on /services finder
            </label>
          </div>
          <div className="flex justify-between items-center pt-1">
            <div className="text-[10px] text-[#666] font-mono">key: {row.key || "(unassigned)"}</div>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => onChange(move(rows, idx, -1))}
                disabled={idx === 0}
                className="w-7 h-7 rounded-md border border-[#262626] grid place-items-center disabled:opacity-30 hover:border-[#FF6600]"
              ><ArrowUp className="w-3.5 h-3.5" /></button>
              <button
                type="button"
                onClick={() => onChange(move(rows, idx, 1))}
                disabled={idx === rows.length - 1}
                className="w-7 h-7 rounded-md border border-[#262626] grid place-items-center disabled:opacity-30 hover:border-[#FF6600]"
              ><ArrowDown className="w-3.5 h-3.5" /></button>
              <button
                type="button"
                onClick={() => onChange(rows.filter((_, k) => k !== idx))}
                className="w-7 h-7 rounded-md border border-[#262626] grid place-items-center hover:border-red-500 hover:text-red-500"
                data-testid={`${testId}-row-${idx}-remove`}
              ><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...rows, newFinderItem()])}
        className="w-full border border-dashed border-[#262626] hover:border-[#FF6600] rounded-xl py-3 text-sm text-[#A3A3A3] hover:text-[#FF6600] inline-flex items-center justify-center gap-2"
        data-testid={`${testId}-add`}
      >
        <Plus className="w-4 h-4" /> Add finder tile
      </button>
    </div>
  );
};

// ---------- Main drawer ----------
export const ServiceEditorDrawer = ({ item, onClose, onSave, ImageUploadField }) => {
  const [form, setForm] = useState(() => {
    const merged = {
      slug: "",
      name: "",
      tagline: "",
      byline: { text: "", bold: true, italic: false, size: "small" },
      icon: "Sparkles",
      image: "",
      short_description: "",
      long_description: "",
      checklist_items: [],
      features: [],
      cta_label: "Build Your Package",
      cta_secondary_label: "View Examples",
      pricing_model: "tiered",
      tiers: [],
      addons: [],
      standalone_items: [],
      kits: [],
      display_order: 0,
      active: true,
      featured: false,
      finder_items: [],
      ...(item || {}),
    };
    // Normalize byline in case it's still a legacy string
    const b = merged.byline;
    if (!b || typeof b === "string") {
      merged.byline = { text: typeof b === "string" ? b : "", bold: true, italic: false, size: "small" };
    }
    return merged;
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name || !form.slug) return;
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  const pm = form.pricing_model || "tiered";
  const showTiers = pm === "tiered" || pm === "volume_tiered";
  const showItems = pm === "custom_items" || pm === "kits";
  const showKits = pm === "kits";
  const qtyScheduleInvalid = hasInvalidQuantityPricing(form.standalone_items);

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-end" onClick={onClose}>
      <aside className="bg-[#0F0F0F] w-full max-w-2xl h-full border-l border-[#262626] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0F0F0F] border-b border-[#262626] px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-display text-xl font-bold">{item?.id ? "Edit Service" : "Create Service"}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full border border-[#262626] grid place-items-center"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basics */}
          <Section
            variant="subtle"
            title="Basics"
            destination="Renders on: /services grid card AND /services/[slug] product-page hero"
          >
            <div className="grid grid-cols-2 gap-3">
              <Field label="Slug" hint="URL-safe, unique"><Input value={form.slug} onChange={(e) => set("slug")(e.target.value)} placeholder="branding-identity" /></Field>
              <Field label="Icon" hint="Lucide icon name (e.g. Palette, Rocket)"><Input value={form.icon} onChange={(e) => set("icon")(e.target.value)} placeholder="Palette" /></Field>
            </div>
            <Field label="Name"><Input value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder="Branding & Identity" /></Field>
            <Field label="Tagline" hint="One-line disambiguating subtitle shown under the title"><Input value={form.tagline} onChange={(e) => set("tagline")(e.target.value)} placeholder="We define your brand." /></Field>
            <BylineEditor value={form.byline} onChange={(v) => set("byline")(v)} />
            <Field label="Short description"><Textarea value={form.short_description} onChange={(e) => set("short_description")(e.target.value)} rows={2} /></Field>
            <Field label="Long description"><Textarea value={form.long_description} onChange={(e) => set("long_description")(e.target.value)} rows={4} /></Field>
            {ImageUploadField && (
              <Field label="Hero image">
                <ImageUploadField value={form.image || ""} onChange={set("image")} testId="upload-service-image" />
              </Field>
            )}
          </Section>

          {/* Visibility + CTAs */}
          <Section
            variant="subtle"
            title="Display"
            destination="Renders on: /services grid + Homepage → Services Section (via Featured toggle) + CTA labels used on both surfaces"
          >
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.active !== false} onChange={(e) => set("active")(e.target.checked)} className="w-4 h-4 accent-[#FF6600]" />
                Active <span className="text-xs text-[#7A7A7A]">(hides from Services page + configurator if off)</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured")(e.target.checked)} className="w-4 h-4 accent-[#FF6600]" />
                Featured on homepage
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Primary CTA label" hint="e.g. 'Build Your Package' — the orange button"><Input value={form.cta_label} onChange={(e) => set("cta_label")(e.target.value)} /></Field>
              <Field label="Secondary CTA label" hint="e.g. 'View Examples' — the outlined button next to it"><Input value={form.cta_secondary_label} onChange={(e) => set("cta_secondary_label")(e.target.value)} /></Field>
            </div>
          </Section>

          {/* Checklist */}
          <Section
            variant="subtle"
            title="Checklist Bullets"
            destination="Renders on: /services grid card + /services/[slug] product page (orange-check bullet list)"
            hint="Short strings shown with a checkmark directly on the service card. Independent of tiers/items."
          >
            <ChecklistEditor items={form.checklist_items} onChange={set("checklist_items")} />
          </Section>

          {/* Finder Shortcuts (Phase 2 CMS Reconciliation) */}
          <Section
            variant="subtle"
            title="Finder Shortcuts"
            destination="Renders on: Homepage → Services Finder (max 7 tiles) AND /services → Finder grid (max 13 tiles). Icons live in /public/finder-icons/*.png"
            hint="Each row is one tile in the icon+label finder. Toggle per surface (Home / Services) and use Focus Param for deep-links into a specific option family inside this service's configurator."
          >
            <FinderItemsEditor
              items={form.finder_items}
              onChange={set("finder_items")}
              testId="admin-finder-items"
            />
          </Section>

          {/* Pricing Model */}
          <Section
            variant="subtle"
            title="Pricing Model"
            destination="Renders on: /services/[slug] configurator (determines which steps + sub-editors below appear for the customer)"
          >
            <Field hint="Determines which sub-editor is shown below and which builder mode renders on the customer-facing page.">
              <select
                value={pm}
                onChange={(e) => set("pricing_model")(e.target.value)}
                data-testid="field-pricing_model"
                className="mt-2 w-full bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#FF6600]"
              >
                {PRICING_MODELS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </Field>
          </Section>

          {/* Conditional editors */}
          {showTiers && (
            <>
              <Section
                variant="subtle"
                title="Tiers"
                destination="Renders on: /services/[slug] configurator → Step 1 tier picker (customer picks ONE)"
              >
                <TiersEditor tiers={form.tiers} onChange={set("tiers")} />
              </Section>
              <Section
                variant="subtle"
                title="Add-ons"
                destination="Renders on: /services/[slug] configurator → Step 2 add-ons list (customer picks multiple)"
              >
                <AddonsEditor addons={form.addons} tiers={form.tiers} onChange={set("addons")} />
              </Section>
            </>
          )}
          {showItems && (
            <Section
              variant="subtle"
              title={<>Standalone Items {showKits && <span className="text-[10px] text-[#7A7A7A] normal-case tracking-normal">(pool used by kits below)</span>}</>}
              destination={`Renders on: /services/[slug] configurator → flat priced checklist${showKits ? " (source pool for the kits below)" : ""}`}
            >
              <StandaloneItemsEditor items={form.standalone_items} onChange={set("standalone_items")} />
            </Section>
          )}
          {showKits && (
            <Section
              variant="subtle"
              title="Kits"
              destination="Renders on: /services/[slug] configurator → named bundles (customer picks a kit + optionally more standalone items)"
            >
              <KitsEditor kits={form.kits} items={form.standalone_items || []} onChange={set("kits")} />
            </Section>
          )}

          <div className="pt-4">
            <button data-testid="admin-save" onClick={handleSave} disabled={saving || !form.name || !form.slug || qtyScheduleInvalid} className="ur-btn-primary w-full disabled:opacity-40">
              {saving ? "Saving…" : "Save Service"} <Save className="w-4 h-4" />
            </button>
            {(!form.name || !form.slug) && <p className="text-xs text-[#7A7A7A] mt-2 text-center">Name and slug are required.</p>}
            {qtyScheduleInvalid && <p className="text-xs text-red-400 mt-2 text-center" data-testid="admin-save-blocked-qty-pricing">Fix the quantity-pricing schedule error(s) above to save.</p>}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default ServiceEditorDrawer;
